import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Building2, Check, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/toaster.jsx';
import { API_BASE_URL } from '@/lib/api';
import { apiFetch } from '@/lib/apiFetch';

/**
 * Combobox de búsqueda de proveedores.
 *
 * Usa el endpoint `POST /suppliers/filter` con un query regex
 * case-insensitive sobre `nombre` y `nombreComercial`.
 *
 * Props:
 *  - value: proveedor actualmente seleccionado (id + nombre + ...)
 *  - onChange: (supplier | null) => void
 *  - defaultQuery: término inicial (ej. el que extrajo la IA)
 *  - placeholder, className
 *  - autoOpen: si true, abre el dropdown al montar
 */
const SupplierCombobox = ({
  value = null,
  onChange,
  defaultQuery = '',
  placeholder = 'Buscar proveedor...',
  className = '',
  autoOpen = false,
}) => {
  const toast = useToast();
  const [query, setQuery] = useState(defaultQuery);
  const [open, setOpen] = useState(autoOpen);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Si cambia el proveedor seleccionado desde afuera, sincronizar
  useEffect(() => {
    if (value?.nombre && value.nombre !== query) {
      setQuery(value.nombre);
    }
  }, [value?.nombre]);

  // Click outside para cerrar
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const search = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    setIsLoading(true);
    try {
      const rx = { $regex: term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), $options: 'i' };
      // Con `API_BASE_URL` y `apiFetch`, como el resto de los servicios.
      // Antes era `fetch('/api/suppliers/filter')`: una URL relativa que
      // le pegaba al servidor del FRONT (Vite, sin proxy) y daba 404, así
      // que el buscador nunca devolvía proveedores.
      const res = await apiFetch(`${API_BASE_URL}/suppliers/filter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: {
            estado: 'Activo',
            $or: [{ nombre: rx }, { nombreComercial: rx }],
          },
          limit: 8,
          offset: 1,
          sort: { nombre: 1 },
        }),
      });
      if (!res.ok) throw new Error(`El servidor respondió ${res.status}`);
      const data = await res.json();
      const list = data?.data || data?.items || data || [];
      setResults(Array.isArray(list) ? list : []);
      setHighlighted(0);
    } catch (e) {
      toast.error('Error buscando proveedores', e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Debounce de búsqueda
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      search(query);
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  const select = (sup) => {
    onChange?.({
      id: sup._id || sup.id,
      nombre: sup.nombre,
      nombreComercial: sup.nombreComercial || '',
      identificacionTipo: sup.identificacionTipo || '',
      identificacionNumero: sup.identificacionNumero || '',
      correo: sup.correo || '',
      telefono: sup.telefono || '',
    });
    setQuery(sup.nombre);
    setOpen(false);
  };

  const clear = () => {
    onChange?.(null);
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlighted]) select(results[highlighted]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="h-9 pl-8 pr-8"
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
            onClick={clear}
            tabIndex={-1}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <AnimatePresence>
        {open && (query.length >= 2) && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-72 overflow-y-auto"
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Buscando proveedores...
              </div>
            ) : results.length === 0 ? (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                No se encontraron proveedores con ese nombre.
              </div>
            ) : (
              <ul className="py-1">
                {results.map((sup, idx) => (
                  <li
                    key={sup._id || sup.id}
                    onClick={() => select(sup)}
                    onMouseEnter={() => setHighlighted(idx)}
                    className={cn(
                      'px-3 py-2 cursor-pointer flex items-start gap-2',
                      idx === highlighted && 'bg-muted',
                    )}
                  >
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {sup.nombre}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {[
                          sup.nombreComercial,
                          sup.identificacionNumero,
                          sup.ciudad,
                          sup.pais,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    {value?.id === (sup._id || sup.id) && (
                      <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupplierCombobox;
